/**
 * Scroll the first invalid field on the form into view and focus it.
 * Call this after a failed `validate()` in any form's submit handler so
 * a director who clicks Save at the bottom of a long form jumps right
 * to the first missing required field instead of having to scroll back
 * up to find it.
 *
 * Uses requestAnimationFrame so React has a tick to render the
 * has-error class / .error span before we query the DOM. The selector
 * matches every error pattern in use across the portal:
 *   • .field.has-error           — set by FormField components
 *   • .field:has(.error)         — manual .field wrappers with an
 *                                  inline error message
 *   • .field:has(+ .error)       — error spans rendered as a sibling
 *                                  of .field (MyAccount-style)
 *
 * Pass an optional container element to scope the lookup; otherwise
 * the whole document is searched. Modal-based forms generally don't
 * need to scope since only one form is mounted at a time.
 */
export function scrollToFirstError(container) {
  const root = container || (typeof document !== 'undefined' ? document : null);
  if (!root) return;
  // Defer until after React commits the error classes — without this
  // the query runs before .has-error is applied and finds nothing.
  const run = () => {
    const errorField = root.querySelector(
      '.field.has-error, .field:has(.error), .field:has(+ .error)',
    );
    if (!errorField) return;
    // `block: 'nearest'` only scrolls if the field is actually
    // offscreen — and when it does, it scrolls just enough to bring
    // the field flush with the nearest edge. That avoids the modal
    // bug where 'center' left the top of the modal's scrollable
    // area blank because the browser tried to center a field that
    // didn't have enough room above it inside the modal viewport.
    // Skipping `behavior: 'smooth'` makes the jump instant — no
    // half-rendered intermediate frames where the modal looks empty.
    errorField.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    // Focus the input inside so the user can immediately start typing.
    // preventScroll stops the browser from doing a second auto-scroll
    // on focus that could fight with the one we just performed.
    const input = errorField.querySelector('input, select, textarea');
    if (input && typeof input.focus === 'function') {
      try {
        input.focus({ preventScroll: true });
      } catch {
        // Older browsers without preventScroll support — fall back
        // to a plain focus call.
        input.focus();
      }
    }
  };
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(run);
  } else {
    setTimeout(run, 0);
  }
}
